/* ============
 * Actions for the carePlan module
 * ============
 *
 * The actions that are available on the
 * carePlan module.
 */
import Vue from 'vue';
import CarePlanProxy from '@/proxies/CarePlanProxy';
import OrganisationProxy from '@/proxies/OrganisationProxy';
import CarePlanTemplateProxy from '@/proxies/CarePlanTemplateProxy';

import AssessmentProxy from '@/proxies/AssessmentProxy';
import InstructionProxy from '@/proxies/InstructionProxy';
import InterventionProxy from '@/proxies/InterventionProxy';
import message from '@/constants/_message';

import types from './mutation-types';

export const getCarePlans = ({ commit }, data) => {
  commit(types.FETCH_CAREPLAN_LIST);
  new CarePlanProxy().fetchCarePlans(data).then(({ careplan: { totalCount, carePlans } }) => {
    commit(types.FETCH_CAREPLAN_LIST_SUCCESS, {
      totalCount,
      carePlans
    });
  }).catch((error) => {
    commit(types.FETCH_CAREPLAN_LIST_FAIL, error);
  });
};

export const searchCarePlans = ({ commit }, data) => {
  commit(types.SEARCH_CAREPLAN);
  new CarePlanProxy().searchCarePlans(data).then(({ careplan }) => {
    commit(types.SEARCH_CAREPLAN_SUCCESS, careplan);
  }).catch((error) => {
    commit(types.SEARCH_CAREPLAN_FAIL, error);
  });
};

export const create = ({ commit }, data) => {
  commit(types.CREATE_CAREPLAN);
  new OrganisationProxy().create(data).then((response) => {
    commit(types.CREATE_CAREPLAN_SUCCESS, response);
    Vue.router.push({
      name: 'careplan.view',
      params: { id: response.careplan.id }
    });
    Vue.notify({
      group: 'notification',
      title: 'Success',
      text: message.CAREPLAN_CREATED,
      type: 'success'
    });
  }).catch((error) => {
    commit(types.CREATE_CAREPLAN_FAIL, error);
    Vue.notify({
      group: 'notification',
      title: 'Error',
      text: message.CAREPLAN_NOT_CREATED,
      type: 'error'
    });
  });
};

export const update = ({ commit }, { data, id }) => {
  commit(types.CREATE_CAREPLAN);
  new CarePlanProxy().update({ data, id }).then((response) => {
    commit(types.CREATE_CAREPLAN_SUCCESS, response);
    Vue.router.push({
      name: 'careplan.view',
      params: { id: response.id }
    });
    Vue.notify({
      group: 'notification',
      title: 'Success',
      text: message.CAREPLAN_UPDATED,
      type: 'success'
    });
  }).catch((error) => {
    commit(types.CREATE_CAREPLAN_FAIL, error);
    Vue.notify({
      group: 'notification',
      title: 'Error',
      text: message.CAREPLAN_NOT_UPDATED,
      type: 'error'
    });
  });
};

export const remove = ({ commit }, obj) => {
  commit(types.DELETE_CAREPLAN);
  new CarePlanProxy().update(obj).then((response) => {
    commit(types.DELETE_CAREPLAN_SUCCESS, response.id);
    Vue.notify({
      group: 'notification',
      title: 'Success',
      text: message.CAREPLAN_DELETED,
      type: 'success'
    });
  }).catch((error) => {
    commit(types.DELETE_CAREPLAN_FAIL, error);
    Vue.notify({
      group: 'notification',
      title: 'Error',
      text: message.CAREPLAN_NOT_DELETED,
      type: 'error'
    });
  });
};

export const view = ({ commit }, data) => {
  commit(types.GET_CAREPLAN);
  return new Promise((resolve, reject) => {
    new CarePlanProxy().view(data).then((response) => {
      commit(types.GET_CAREPLAN_SUCCESS, response);
      resolve();
    }).catch((error) => {
      commit(types.GET_CAREPLAN_FAIL, error);
      reject();
    });
  });
};

export const createIntervention = ({ commit }, { id, data }) => {
  commit(types.CREATE_INTERVENTION);
  new CarePlanProxy().createIntervention(id, data).then(({ intervention }) => {
    commit(types.CREATE_INTERVENTION_SUCCESS, intervention);
    Vue.notify({
      group: 'notification',
      title: 'Success',
      text: message.INTERVENTION_CREATED,
      type: 'success'
    });
  }).catch((error) => {
    commit(types.CREATE_INTERVENTION_FAIL, error);
    Vue.notify({
      group: 'notification',
      title: 'Error',
      text: message.INTERVENTION_NOT_CREATED,
      type: 'error'
    });
  });
};

export const createAssessment = ({ commit }, { id, data }) => {
  commit(types.CREATE_ASSESSMENT);
  new CarePlanProxy().createAssessment(id, data).then(({ assessment }) => {
    commit(types.CREATE_ASSESSMENT_SUCCESS, assessment);
    Vue.notify({
      group: 'notification',
      title: 'Success',
      text: message.ASSESSMENT_CREATED,
      type: 'success'
    });
  }).catch((error) => {
    commit(types.CREATE_ASSESSMENT_FAIL, error);
    Vue.notify({
      group: 'notification',
      title: 'Error',
      text: message.ASSESSMENT_NOT_CREATED,
      type: 'error'
    });
  });
};

export const createInstruction = ({ commit }, { id, data }) => {
  commit(types.CREATE_INSTRUCTION);
  new CarePlanProxy().createInstruction(id, data).then(({ instruction }) => {
    commit(types.CREATE_INSTRUCTION_SUCCESS, instruction);
    Vue.notify({
      group: 'notification',
      title: 'Success',
      text: message.INSTRUCTION_CREATED,
      type: 'success'
    });
  }).catch((error) => {
    commit(types.CREATE_INSTRUCTION_FAIL, error);
    Vue.notify({
      group: 'notification',
      title: 'Error',
      text: message.INSTRUCTION_NOT_CREATED,
      type: 'error'
    });
  });
};

export const getTemplates = ({ commit }) => {
  commit(types.IMPORT_CAREPLAN_TEMPLATE);
  new CarePlanTemplateProxy().fetchTemplates().then(({ careplan }) => {
    commit(types.IMPORT_CAREPLAN_TEMPLATE_SUCCESS, careplan);
  }).catch((error) => {
    commit(types.IMPORT_CAREPLAN_TEMPLATE_FAIL, error);
  });
};

export const getTemplate = ({ commit }, id) => {
  commit(types.FETCH_CAREPLAN_TEMPLATE);
  new CarePlanTemplateProxy().fetchTemplate(id).then((response) => {
    commit(types.FETCH_CAREPLAN_TEMPLATE_SUCCESS, response);
  }).catch((error) => {
    commit(types.FETCH_CAREPLAN_TEMPLATE_FAIL, error);
  });
};

export const updateIntervention = ({ commit }, { id, data }) => {
  commit(types.UPDATE_INTERVENTION);
  new InterventionProxy().updateIntervention(id, data).then((intervention) => {
    commit(types.UPDATE_INTERVENTION_SUCCESS, intervention);
    Vue.notify({
      group: 'notification',
      title: 'Success',
      text: message.INTERVENTION_UPDATED,
      type: 'success'
    });
  }).catch((error) => {
    commit(types.UPDATE_INTERVENTION_FAIL, error);
    Vue.notify({
      group: 'notification',
      title: 'Error',
      text: message.INTERVENTION_NOT_UPDATED,
      type: 'error'
    });
  });
};

export const updateAssessment = ({ commit }, { id, data }) => {
  commit(types.UPDATE_ASSESSMENT);
  new AssessmentProxy().updateAssessment(id, data).then((response) => {
    commit(types.UPDATE_ASSESSMENT_SUCCESS, response.assessment);
    Vue.notify({
      group: 'notification',
      title: 'Success',
      text: message.ASSESSMENT_UPDATED,
      type: 'success'
    });
  }).catch((error) => {
    commit(types.UPDATE_ASSESSMENT_FAIL, error);
    Vue.notify({
      group: 'notification',
      title: 'Error',
      text: message.ASSESSMENT_NOT_UPDATED,
      type: 'error'
    });
  });
};

export const updateInstruction = ({ commit }, { id, data }) => {
  commit(types.UPDATE_INSTRUCTION);
  new InstructionProxy().updateInstruction(id, data).then((instruction) => {
    commit(types.UPDATE_INSTRUCTION_SUCCESS, instruction);
    Vue.notify({
      group: 'notification',
      title: 'Error',
      text: message.INSTRUCTION_UPDATED,
      type: 'success'
    });
  }).catch((error) => {
    commit(types.UPDATE_INSTRUCTION_FAIL, error);
    Vue.notify({
      group: 'notification',
      title: 'Error',
      text: message.INSTRUCTION_NOT_UPDATED,
      type: 'error'
    });
  });
};

export const createCarePlan = ({ commit }, id) => {
  commit(types.CREATE_CAREPLAN_FROM_TEMPLATE);
  new CarePlanProxy().createCarePlanFromTemplate(id).then((response) => {
    commit(types.CREATE_CAREPLAN_FROM_TEMPLATE_SUCCESS, response);
    Vue.router.push({
      name: 'careplan.view',
      params: { id: response.careplan.id }
    });
    Vue.notify({
      group: 'notification',
      title: 'Success',
      text: message.CAREPLAN_CREATED,
      type: 'success'
    });
  }).catch((error) => {
    commit(types.CREATE_CAREPLAN_FROM_TEMPLATE_FAIL, error);
    Vue.notify({
      group: 'notification',
      title: 'Error',
      text: message.CAREPLAN_NOT_CREATED,
      type: 'error'
    });
  });
};

export const deleteIntervention = ({ commit }, { id, data }) => {
  commit(types.DELETE_INTERVENTION);
  new InterventionProxy().updateIntervention(id, data).then((intervention) => {
    commit(types.DELETE_INTERVENTION_SUCCESS, intervention);
    Vue.notify({
      group: 'notification',
      title: 'Success',
      text: message.INTERVENTION_DELETED,
      type: 'success'
    });
  }).catch((error) => {
    commit(types.DELETE_INTERVENTION_FAIL, error);
    Vue.notify({
      group: 'notification',
      title: 'Error',
      text: message.INTERVENTION_NOT_DELETED,
      type: 'error'
    });
  });
};

export const deleteAssessment = ({ commit }, { id, data }) => {
  commit(types.DELETE_ASSESSMENT);
  new AssessmentProxy().deleteAssessment(id, data).then((assessment) => {
    commit(types.DELETE_ASSESSMENT_SUCCESS, assessment);
    Vue.notify({
      group: 'notification',
      title: 'Success',
      text: message.ASSESSMENT_DELETED,
      type: 'success'
    });
  }).catch((error) => {
    commit(types.DELETE_ASSESSMENT_FAIL, error);
    Vue.notify({
      group: 'notification',
      title: 'Error',
      text: message.ASSESSMENT_NOT_DELETED,
      type: 'error'
    });
  });
};

export const deleteInstruction = ({ commit }, { id, data }) => {
  commit(types.DELETE_INSTRUCTION);
  new InstructionProxy().updateInstruction(id, data).then((instruction) => {
    commit(types.DELETE_INSTRUCTION_SUCCESS, instruction);
    Vue.notify({
      group: 'notification',
      title: 'Success',
      text: message.INSTRUCTION_DELETED,
      type: 'success'
    });
  }).catch((error) => {
    commit(types.DELETE_INSTRUCTION_FAIL, error);
    Vue.notify({
      group: 'notification',
      title: 'Error',
      text: message.INSTRUCTION_NOT_DELETED,
      type: 'error'
    });
  });
};

export default {
  getCarePlans,
  create,
  view,
  update,
  remove,
  createIntervention,
  createAssessment,
  createInstruction,
  getTemplates,
  getTemplate,
  updateIntervention,
  updateAssessment,
  updateInstruction,
  createCarePlan,
  searchCarePlans,
  deleteIntervention,
  deleteAssessment,
  deleteInstruction
};
